import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Mic, Volume2, StopCircle } from 'lucide-react';
import { Button } from './ui/Button';
type Message = {
  role: 'user' | 'assistant' | 'system';
  content: string;
};
const SYSTEM_PROMPT = `
Eres un asistente de ventas experto y persuasivo para "Ansiedad Exprés".
Tu objetivo es CERRAR LA VENTA. Sé proactivo, haz preguntas y lleva al cliente al pago.

DETALLES DEL PRODUCTO:
- Nombre: Ansiedad Exprés
- Precio: $111.99 MXN (pago único)
- Promesa: Protocolo de 5 minutos para detener la ansiedad.
- Enlace de compra: https://pay.hotmart.com/D104310882V?bid=1770407336992

ESTRATEGIA DE VENTAS:
1. Empatiza rápidamente ("Entiendo lo difícil que es sentir que pierdes el control").
2. Presenta la solución como única y mecánica ("No es teoría, es un freno de mano fisiológico").
3. CIERRA LA VENTA: "Por solo $111.99 MXN puedes recuperar tu paz hoy mismo. ¿Te gustaría probarlo?"
4. Si preguntan cómo comprar, dales el enlace DIRECTAMENTE: "Aquí tienes el enlace seguro para descarga inmediata: https://pay.hotmart.com/D104310882V?bid=1770407336992"

TONO:
- Cálido pero con autoridad.
- Usa emojis ocasionalmente.
- Respuestas CORTAS (máximo 2 oraciones) para que el audio sea fluido.
- IDIOMA: Español neutro.
`;
const WELCOME_MESSAGE = '¡Hola! 👋 Soy tu asistente personal. ¿Tienes alguna duda sobre Ansiedad Exprés? Estoy aquí para ayudarte a resolver cualquier pregunta.';
export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: WELCOME_MESSAGE
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<any>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) setShowTooltip(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => setShowTooltip(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.lang = 'es-MX';
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          setInput(transcript);
          setIsListening(false);
          setTimeout(() => handleSend(transcript), 500);
        };
        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);
        recognitionRef.current = recognition;
      }
    }
  }, []);
  const speakText = async (text: string) => {
    try {
      const apiKey = (window as any).__MP_OPENAI_API_KEY;
      if (!apiKey) return;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(true);
      const response = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'tts-1',
          voice: 'nova',
          input: text,
          response_format: 'mp3'
        })
      });
      if (!response.ok) throw new Error('TTS Error');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(url);
      };
      audioRef.current = audio;
      await audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
    }
  };
  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };
  const handleSend = async (overrideInput?: string) => {
    const textToSend = overrideInput || input;
    if (!textToSend.trim() || isLoading) return;
    if (audioRef.current) {
      audioRef.current.pause();
      setIsSpeaking(false);
    }
    const userMessage: Message = {
      role: 'user',
      content: textToSend
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const apiKey = (window as any).__MP_OPENAI_API_KEY;
      if (!apiKey) throw new Error('API Key no configurada');
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: SYSTEM_PROMPT
          }, ...messages.map(m => ({
            role: m.role,
            content: m.content
          })), {
            role: 'user',
            content: textToSend
          }],
          max_tokens: 150,
          temperature: 0.7
        })
      });
      if (!response.ok) throw new Error('OpenAI Error');
      const data = await response.json();
      const assistantMessage = data.choices[0].message.content;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: assistantMessage
      }]);
      speakText(assistantMessage);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Lo siento, hubo un error de conexión.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleOpen = () => {
    setIsOpen(true);
    setShowTooltip(false);
  };
  return <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end" data-id="element-52">
      {/* Chat Window */}
      <AnimatePresence data-id="element-53">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.25
      }} className="mb-4 w-[350px] max-w-[calc(100vw-48px)] h-[500px] max-h-[70vh] bg-white/98 border border-[#E8E8E6] rounded-2xl shadow-xl flex flex-col overflow-hidden" data-id="element-54">
            {/* Header */}
            <div className="bg-[#F7F7F5] p-4 flex items-center justify-between border-b border-[#E8E8E6]" data-id="element-55">
              <div className="flex items-center gap-2" data-id="element-56">
                {isSpeaking ? <div className="flex items-end gap-0.5 h-4" data-id="element-57">
                    <motion.div animate={{
                height: [4, 12, 4]
              }} transition={{
                repeat: Infinity,
                duration: 0.5
              }} className="w-1 bg-[#5BBFB5] rounded-full" data-id="element-58" />
                    <motion.div animate={{
                height: [4, 16, 4]
              }} transition={{
                repeat: Infinity,
                duration: 0.4,
                delay: 0.1
              }} className="w-1 bg-[#5BBFB5] rounded-full" data-id="element-59" />
                    <motion.div animate={{
                height: [4, 10, 4]
              }} transition={{
                repeat: Infinity,
                duration: 0.6,
                delay: 0.2
              }} className="w-1 bg-[#5BBFB5] rounded-full" data-id="element-60" />
                  </div> : <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-61" />}
                <h3 className="font-semibold text-[#3A3A3A] text-sm" data-id="element-62">
                  Asistente Ansiedad Exprés
                </h3>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-[#7A7A7A] hover:text-[#3A3A3A] transition-colors" data-id="element-63">
                <X className="w-5 h-5" data-id="element-64" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-65">
              {messages.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-66">
                  <div className={`max-w-[85%] p-3 text-sm leading-relaxed relative group ${msg.role === 'user' ? 'bg-[#5BBFB5] text-white rounded-2xl rounded-br-sm shadow-sm' : 'bg-[#F7F7F5] text-[#3A3A3A] border border-[#E8E8E6] rounded-2xl rounded-bl-sm'}`} data-id="element-67">
                    {msg.content}
                    {msg.role === 'assistant' && <button onClick={() => speakText(msg.content)} className="absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-[#7A7A7A] hover:text-[#5BBFB5]" data-id="element-68">
                        <Volume2 className="w-4 h-4" data-id="element-69" />
                      </button>}
                  </div>
                </div>)}
              {isLoading && <div className="flex justify-start" data-id="element-70">
                  <div className="bg-[#F7F7F5] p-3 rounded-2xl rounded-bl-sm border border-[#E8E8E6] flex gap-1" data-id="element-71">
                    <span className="w-1.5 h-1.5 bg-[#7A7A7A] rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-72" />
                    <span className="w-1.5 h-1.5 bg-[#7A7A7A] rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-73" />
                    <span className="w-1.5 h-1.5 bg-[#7A7A7A] rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-74" />
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-75" />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-[#E8E8E6] bg-white" data-id="element-76">
              <form onSubmit={e => {
            e.preventDefault();
            handleSend();
          }} className="flex gap-2" data-id="element-77">
                <div className="relative flex-1" data-id="element-78">
                  <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder={isListening ? 'Escuchando...' : 'Escribe o habla...'} className={`w-full bg-white border border-[#E8E8E6] rounded-full pl-4 pr-10 py-2 text-sm focus:outline-none focus:border-[#5BBFB5] focus:ring-1 focus:ring-[#5BBFB5]/20 transition-all ${isListening ? 'border-red-400 ring-1 ring-red-200' : ''}`} disabled={isLoading || isListening} data-id="element-79" />
                  {recognitionRef.current && <button type="button" onClick={toggleListening} className={`absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${isListening ? 'bg-red-500 text-white animate-pulse' : 'text-[#7A7A7A] hover:text-[#3A3A3A] hover:bg-[#F7F7F5]'}`} data-id="element-80">
                      {isListening ? <StopCircle className="w-4 h-4" data-id="element-81" /> : <Mic className="w-4 h-4" data-id="element-82" />}
                    </button>}
                </div>
                <Button type="submit" size="sm" variant="secondary" className="!p-2 !w-10 !h-10 !rounded-full !min-w-0 flex items-center justify-center shrink-0" disabled={isLoading || !input.trim() && !isListening} data-id="element-83">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-84" /> : <Send className="w-4 h-4 ml-0.5" data-id="element-85" />}
                </Button>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Tooltip bubble */}
      <AnimatePresence data-id="element-86">
        {showTooltip && !isOpen && <motion.div initial={{
        opacity: 0,
        x: 10
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 10
      }} transition={{
        duration: 0.3
      }} className="mb-3 mr-2 bg-white border border-[#E8E8E6] rounded-2xl shadow-sm px-4 py-3 max-w-[220px] cursor-pointer" onClick={handleOpen} data-id="element-87">
            <p className="text-sm text-[#3A3A3A] font-medium leading-snug" data-id="element-88">
              💬 ¿Necesitas ayuda?{' '}
              <span className="text-[#5BBFB5] font-bold" data-id="element-89">¡Habla conmigo!</span>
            </p>
            <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-[#E8E8E6] rotate-45" data-id="element-90" />
          </motion.div>}
      </AnimatePresence>

      {/* Floating Button with Serpentine Glow */}
      <div className="relative" data-id="element-91">
        {!isOpen && <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute -inset-1.5 rounded-full bg-gradient-to-tr from-[#5BBFB5] via-white to-[#5BBFB5] opacity-50 blur-sm" data-id="element-92" />}

        <motion.button onClick={() => {
        isOpen ? setIsOpen(false) : handleOpen();
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="w-14 h-14 rounded-full bg-[#5BBFB5] text-white shadow-lg shadow-[#5BBFB5]/20 flex items-center justify-center relative z-50 hover:bg-[#4DAF9F] transition-colors" data-id="element-93">
          {isOpen ? <X className="w-6 h-6" data-id="element-94" /> : <>
              <MessageCircle className="w-7 h-7" data-id="element-95" />
              <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse" data-id="element-96" />
            </>}
        </motion.button>
      </div>
    </div>;
}